/** Read-only catalog of terminal inquiry flows for admin Flow Inspector (Phase 1). */
import { listCustomCatalogSteps } from './terminal-flow-custom.js';

function step(key, label, ask, opts = {}) {
  const editable = ['ask', 'hint'];
  if (opts.choices) editable.push('choices');
  if (opts.behaviorEditable) editable.push('optional');
  return {
    key,
    label,
    ask,
    hint: opts.hint || '',
    choices: opts.choices || '',
    optional: !!opts.optional,
    locked: !!opts.locked,
    behaviorEditable: !!opts.behaviorEditable,
    writesTo: opts.writesTo || '',
    showsWhen: opts.showsWhen || 'Always',
    impact: opts.impact || '',
    pattern: !!opts.pattern,
    editable,
  };
}

const TICKET_NEW_CORE = [
  step('inquiry_date', 'Inquiry date', 'Customer inquiry date?', {
    hint: 'Defaults to today',
    optional: true,
    writesTo: 'data.inquiry_date',
  }),
  step('name', 'Contact name', 'Contact name?', { writesTo: 'data.name' }),
  step('phone', 'Mobile', 'Customer mobile number?', { writesTo: 'data.phone' }),
  step('source', 'Lead source', 'Lead source?', { writesTo: 'data.source' }),
  step('pref_transit', 'Flight routing', 'Flight routing?', {
    choices: '1 Direct · 2 Transit · 3 Multi-city',
    locked: true,
    writesTo: 'data.preferences.transit',
  }),
  step('pref_trip_type', 'Trip type', 'Trip type?', {
    choices: '1 One way · 2 Return',
    showsWhen: 'Direct or transit routing',
    writesTo: 'data.trip_type',
  }),
  step('origin', 'From airport', 'From airport (IATA)?', {
    showsWhen: 'Not multi-city',
    writesTo: 'data.origin',
  }),
  step('destination', 'To airport', 'To airport (IATA)?', {
    showsWhen: 'Not multi-city',
    writesTo: 'data.destination',
  }),
  step('pref_transit_via', 'Transit city', 'Preferred transit airport (IATA)?', {
    optional: true,
    showsWhen: 'Routing = Transit',
    writesTo: 'data.preferences.transit_via',
  }),
  step('pref_multi_city', 'Multi-city route', 'Multi-city route — airports in order?', {
    showsWhen: 'Routing = Multi-city',
    writesTo: 'data.preferences.multi_city_route',
  }),
  step('depart_date', 'Travel date', 'Travel date?', { writesTo: 'data.depart_date' }),
  step('return_date', 'Return date', 'Return travel date?', {
    optional: true,
    showsWhen: 'Trip type = Return',
    writesTo: 'data.return_date',
  }),
  step('pax_line', 'Passengers', 'Passengers (ADT/CHD/INF)?', {
    hint: 'e.g. 2/1/0',
    writesTo: 'data.pax',
  }),
  step('pref_baggage', 'Baggage', 'Preferred baggage allowance?', {
    optional: true,
    writesTo: 'data.preferences.baggage',
  }),
];

const TICKET_TRAVELER_PATTERNS = [];

const VISA_NEW = [
  step('name', 'Contact name', 'Contact / applicant name?', { writesTo: 'data.name' }),
  step('phone', 'Mobile', 'Mobile number?', {
    hint: '+94 77 123 4567 or 0771234567',
    writesTo: 'data.phone',
  }),
  step('package', 'Visa package', 'Visa type / package?', {
    writesTo: 'data.package_name',
    impact: 'Unlocks India eVisa purpose/duration when applicable.',
  }),
  step('pax_adt', 'Adults', 'How many adults (ADT)?', { writesTo: 'data.pax.adt' }),
  step('pax_chd', 'Children', 'Children (CHD)?', {
    hint: 'enter to skip',
    optional: true,
    writesTo: 'data.pax.chd',
  }),
  step('cost_adt', 'Cost per adult', 'Cost price per adult (LKR)?', {
    writesTo: 'data.pax_cost_rates.adt',
    impact: 'Pre-fills from admin visa costs when available.',
  }),
  step('cost_chd', 'Cost per child', 'Cost price per child (LKR)?', {
    hint: 'Optional — skip to use adult rate.',
    optional: true,
    showsWhen: 'CHD > 0',
    writesTo: 'data.pax_cost_rates.chd',
  }),
  step('sell_adt', 'Sell per adult', 'Selling price per adult (LKR)?', {
    writesTo: 'data.pax_rates.adt',
    impact: 'Drives lead amount and profit on create.',
  }),
  step('sell_chd', 'Sell per child', 'Selling price per child (LKR)?', {
    hint: 'Optional — skip to use adult rate.',
    optional: true,
    showsWhen: 'CHD > 0',
    writesTo: 'data.pax_rates.chd',
  }),
  step('india_purpose', 'India purpose', 'India eVisa purpose?', {
    choices: '1 Tourist · 2 Business · 3 Other',
    showsWhen: 'India eVisa package',
    writesTo: 'data.india_evisa.purpose',
  }),
  step('india_duration', 'India duration', 'India eVisa duration?', {
    choices: '1 30D · 2 1Y',
    showsWhen: 'India eVisa package',
    writesTo: 'data.india_evisa.duration',
  }),
  step('passport', 'Passport', 'Passport number?', {
    hint: 'enter to skip',
    optional: true,
    writesTo: 'data.passport_no',
  }),
  step('source', 'Lead source', 'Lead source?', { writesTo: 'data.source' }),
  step('note', 'Notes', 'Notes?', { optional: true, behaviorEditable: true, writesTo: 'data.note' }),
];

const TOUR_NEW = [
  step('name', 'Contact name', 'Contact name?', { writesTo: 'data.name' }),
  step('phone', 'Mobile', 'Mobile number?', { writesTo: 'data.phone' }),
  step('package', 'Tour package', 'Tour package?', { writesTo: 'data.package_name' }),
  step('pax_line', 'Pax', 'Pax (ADT/CHD/INF)? — one line e.g. 2/1/0', { writesTo: 'data.pax' }),
  step('date_from', 'Travel from', 'Travel from date?', { writesTo: 'data.date_from, depart_date' }),
  step('date_to', 'Travel to', 'Travel to date?', { optional: true, writesTo: 'data.date_to, return_date' }),
  step('source', 'Lead source', 'Lead source?', { writesTo: 'data.source' }),
  step('brief', 'Brief', 'Requirements / budget / preferences?', {
    optional: true,
    behaviorEditable: true,
    writesTo: 'data.brief, client_brief',
  }),
];

function stageFlow(id, type, status, label, steps) {
  return { id, type, mode: 'stage', status, label, steps };
}

export const TERMINAL_FLOWS = [
  {
    id: 'ticket.new',
    type: 'ticket',
    mode: 'new',
    label: 'Ticket — New inquiry',
    steps: TICKET_NEW_CORE,
    patterns: TICKET_TRAVELER_PATTERNS,
  },
  {
    id: 'visa.new',
    type: 'visa',
    mode: 'new',
    label: 'Visa — New inquiry',
    steps: VISA_NEW,
  },
  {
    id: 'tour.new',
    type: 'tour',
    mode: 'new',
    label: 'Tour — New inquiry',
    steps: TOUR_NEW,
  },
  stageFlow('ticket.stage.quotation_sent', 'ticket', 'quotation_sent', 'Ticket — Quotation sent', [
    step('quoted_at', 'Sent on', 'Quotation sent on (date + time)?', { writesTo: 'stage_data.quoted_at' }),
    step('airline', 'Airline', 'Airline quoted?', { writesTo: 'lead.airline' }),
    step('quote_description', 'Description', 'Brief description of quote sent?', { optional: true }),
    step('cost_adt', 'Cost ADT', 'Net cost per adult (LKR)?', { writesTo: 'extra.pax_cost_rates.adt', showsWhen: 'ADT > 0' }),
    step('profit_adt', 'Profit ADT', 'Profit per adult (LKR)?', { writesTo: 'extra.pax_profit_rates.adt', showsWhen: 'ADT > 0' }),
    step('cost_chd', 'Cost CHD', 'Net cost per child (LKR)?', { writesTo: 'extra.pax_cost_rates.chd', showsWhen: 'CHD > 0', optional: true }),
    step('profit_chd', 'Profit CHD', 'Profit per child (LKR)?', { writesTo: 'extra.pax_profit_rates.chd', showsWhen: 'CHD > 0', optional: true }),
    step('cost_inf', 'Cost INF', 'Net cost per infant (LKR)?', { writesTo: 'extra.pax_cost_rates.inf', showsWhen: 'INF > 0', optional: true }),
    step('profit_inf', 'Profit INF', 'Profit per infant (LKR)?', { writesTo: 'extra.pax_profit_rates.inf', showsWhen: 'INF > 0', optional: true }),
    step('quote_valid_till', 'Valid till', 'Quote valid till?', { optional: true }),
  ]),
  stageFlow('ticket.stage.temporary_booking', 'ticket', 'temporary_booking', 'Ticket — Temporary booking', [
    step('cost_adt', 'Cost ADT', 'Net cost per adult (LKR)?', { writesTo: 'extra.pax_cost_rates.adt', showsWhen: 'Pool / quick intake' }),
    step('profit_adt', 'Profit ADT', 'Profit per adult (LKR)?', { writesTo: 'extra.pax_profit_rates.adt', showsWhen: 'Pool / quick intake' }),
    step('cost_chd', 'Cost CHD', 'Net cost per child (LKR)?', { writesTo: 'extra.pax_cost_rates.chd', showsWhen: 'CHD > 0', optional: true }),
    step('profit_chd', 'Profit CHD', 'Profit per child (LKR)?', { writesTo: 'extra.pax_profit_rates.chd', showsWhen: 'CHD > 0', optional: true }),
    step('cost_inf', 'Cost INF', 'Net cost per infant (LKR)?', { writesTo: 'extra.pax_cost_rates.inf', showsWhen: 'INF > 0', optional: true }),
    step('profit_inf', 'Profit INF', 'Profit per infant (LKR)?', { writesTo: 'extra.pax_profit_rates.inf', showsWhen: 'INF > 0', optional: true }),
    step('pnr', 'PNR', 'PNR / TKT number?', { writesTo: 'lead.pnr' }),
    step('booking_system', 'Booked on', 'Where was the booking made?', { writesTo: 'extra.booking_system' }),
    step('booking_system_other', 'Other source', 'Enter where the booking was made (other)?', { optional: true, showsWhen: 'booking_system = Other' }),
    step('booking_expires', 'Expires', 'Booking expires on (date + time)?', { writesTo: 'extra.booking_expires' }),
    step('advance', 'Advance', 'Advance received (LKR)?', { optional: true }),
  ]),
  stageFlow('ticket.stage.booking_confirmed', 'ticket', 'booking_confirmed', 'Ticket — Booking confirmed (payment)', [
    step('_intro', 'Payment summary', 'Payment stage summary (informational)', {
      optional: true,
      hint: 'Press enter to continue.',
      impact: 'Display only — not saved.',
    }),
    step('paid_on', 'Paid on', 'Payment received on?', { writesTo: 'stage_data.paid_on' }),
    step('payment_amount', 'Payment amount', 'Amount received now (LKR)?', {
      writesTo: 'stage_data.payment_amount, sold_amount',
      impact: 'Accounting ledger entry on finalize.',
    }),
    step('remain_pay_by', 'Balance due by', 'Remaining balance due by?', {
      showsWhen: 'Partial payment (total paid < quoted)',
      writesTo: 'stage_data.remain_pay_by, followup_date',
    }),
    step('note', 'Payment notes', 'Payment notes?', { optional: true }),
  ]),
  stageFlow('ticket.stage.ticket_issued', 'ticket', 'ticket_issued', 'Ticket — Issued', [
    step('issued_date', 'Issued date', 'Ticket issued date?', { writesTo: 'stage_data.issued_date' }),
    step('payment_amount', 'Payment', 'Payment received (LKR)?', { optional: true, writesTo: 'sold_amount' }),
    step('pnr', 'Confirm PNR', 'Confirm PNR / ticket number?', { writesTo: 'lead.pnr' }),
    step('note', 'Notes', 'Notes (e.g. PDF sent)?', { optional: true }),
  ]),
  stageFlow('visa.stage.details_provided', 'visa', 'details_provided', 'Visa — Details provided', [
    step('quote_price', 'Quoted total', 'Total quoted price (LKR) for all passengers?', {
      writesTo: 'lead.amount, stage_data.quote_price',
      impact: 'Syncs profit when cost rates exist.',
    }),
    step('note', 'Client notes', 'Notes for client?', { optional: true, hint: 'enter to skip' }),
  ]),
  stageFlow('visa.stage.visa_applied', 'visa', 'visa_applied', 'Visa — Applied', [
    step('application_id', 'Application ID', 'Application / reference ID?', { writesTo: 'stage_data.application_id' }),
    step('passport_no', 'Passport', 'Passport number?', { writesTo: 'stage_data.passport_no' }),
  ]),
  stageFlow('visa.stage.visa_issued', 'visa', 'visa_issued', 'Visa — Issued', [
    step('issue_date', 'Issue date', 'Visa issue date?', { writesTo: 'stage_data.issue_date' }),
    step('note', 'Notes', 'Notes?', { optional: true }),
  ]),
  stageFlow('tour.stage.quotation', 'tour', 'quotation', 'Tour — Quotation', [
    step('agent_rate_adt', 'Agent cost ADT', 'Agent net cost per adult (LKR)?', { writesTo: 'stage_data.agent_quote.adt, lead.cost_amount' }),
    step('agent_rate_chd', 'Agent cost CHD', 'Agent net cost per child (LKR)?', { optional: true, hint: 'Skip = adult rate', showsWhen: 'CHD > 0' }),
    step('agent_rate_inf', 'Agent cost INF', 'Agent net cost per infant (LKR)?', { optional: true, hint: 'Skip = free', showsWhen: 'INF > 0' }),
    step('flight_cost_adt', 'Flight cost ADT', 'Flight fare per adult (LKR)?', { optional: true }),
    step('flight_cost_chd', 'Flight cost CHD', 'Flight fare per child (LKR)?', { optional: true, showsWhen: 'CHD > 0' }),
    step('flight_cost_inf', 'Flight cost INF', 'Flight fare per infant (LKR)?', { optional: true, showsWhen: 'INF > 0' }),
    step('visa_cost_adt', 'Visa cost ADT', 'Visa fee per adult (LKR)?', { optional: true }),
    step('visa_cost_chd', 'Visa cost CHD', 'Visa fee per child (LKR)?', { optional: true, showsWhen: 'CHD > 0' }),
    step('visa_cost_inf', 'Visa cost INF', 'Visa fee per infant (LKR)?', { optional: true, showsWhen: 'INF > 0' }),
    step('_cost_summary', 'Cost summary', 'Total cost per pax (tour + flight + visa) — auto-summed', { optional: true, locked: true }),
    step('final_rate_adt', 'Final quote ADT', 'Final quote per adult (LKR)?', { writesTo: 'stage_data.final_quote.adt, lead.amount' }),
    step('final_rate_chd', 'Final quote CHD', 'Final quote per child (LKR)?', { optional: true, showsWhen: 'CHD > 0' }),
    step('final_rate_inf', 'Final quote INF', 'Final quote per infant (LKR)?', { optional: true, showsWhen: 'INF > 0' }),
    step('_sell_summary', 'Quote summary', 'Client total, cost total, and profit — auto-calculated', { optional: true, locked: true }),
    step('add_hotel_rooms', 'Hotel rooms', 'Add hotel room details? (Y/N)', { optional: true }),
    step('hotel_rooms_summary', 'Room details', 'Room details? e.g. Double x1 (2 pax)', { optional: true, showsWhen: 'Add hotel = Y' }),
    step('supplier_note', 'Supplier note', 'Supplier / DMC notes?', { optional: true }),
  ]),
  stageFlow('tour.stage.tour_accepted_advance', 'tour', 'tour_accepted_advance', 'Tour — Confirmed & advance', [
    step('travel_date', 'Travel date', 'Travel date?', { writesTo: 'stage_data.travel_date' }),
    step('advance_amount', 'Advance', 'Advance received (LKR)?', {
      writesTo: 'stage_data.advance_amount, advance_paid',
      impact: 'Computes balance due vs final quote.',
    }),
    step('advance_date', 'Advance date', 'Advance paid on?', { writesTo: 'stage_data.advance_date' }),
    step('balance_collect_date', 'Balance collect', 'Balance collect by?', {
      showsWhen: 'Advance < final quote',
      writesTo: 'stage_data.balance_collect_date, followup_date',
    }),
  ]),
  stageFlow('tour.stage.tour_accepted_progress', 'tour', 'tour_accepted_progress', 'Tour — Processing', [
    step('progress_notes', 'Progress notes', 'Progress notes?', { optional: true }),
    step('followup_date', 'Follow-up', 'Next follow-up date?', { optional: true }),
    step('ticket_booked_on', 'Tickets booked', 'Tickets booked on?', { optional: true }),
    step('visa_issued_date', 'Visa issued', 'Visa issued date?', { optional: true, impact: 'Auto-finalise when both ticket & visa dates set.' }),
  ]),
  stageFlow('tour.stage.tour_finalised', 'tour', 'tour_finalised', 'Tour — Finalised', [
    step('note', 'Closing notes', 'Closing notes?', { optional: true }),
  ]),
];

export function findTerminalFlow(flowId) {
  return TERMINAL_FLOWS.find((f) => f.id === flowId) || null;
}

export function listCatalogSteps(flow) {
  if (!flow) return [];
  return [...(flow.steps || []), ...(flow.patterns || []), ...listCustomCatalogSteps(flow.id)];
}

export function findCatalogStep(flowId, stepKey) {
  const flow = findTerminalFlow(flowId);
  if (!flow) return null;
  const direct = listCatalogSteps(flow).find((s) => s.key === stepKey);
  if (direct) return direct;
  if (!stepKey.includes(':')) return null;
  return (flow.patterns || []).find((p) => {
    if (!p.pattern) return false;
    const re = new RegExp(`^${p.key.replace(/\*/g, '[^:]+')}$`);
    return re.test(stepKey);
  }) || null;
}
